import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Button,
  TextField,
} from "@mui/material";
import "react-phone-input-2/lib/bootstrap.css";
import PhoneInput from "react-phone-input-2";
import { useGetCustomerDetailQuery, useUpdateCustomerMutation } from "../../store/api/userAPI";
import Loader from "../../constants/Loader";
import { showToast, showError } from "../../constants/toasts";

const EditUser = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get user ID from location state
  const userId = (location.state as any)?.user?.id || (location.state as any)?.user?._id;

  // Fetch user details
  const { data, isLoading, error } = useGetCustomerDetailQuery(
    { id: userId },
    { skip: !userId }
  );

  const userData = data?.data;

  // Form states
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState("+91");
  const [selectedCountry, setSelectedCountry] = useState("in");
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [country, setCountry] = useState("");
  const [pinCode, setPinCode] = useState("");
  const [isBlocked, setIsBlocked] = useState(false);

  // Update mutation
  const [updateCustomer, { isLoading: isUpdating }] = useUpdateCustomerMutation();

  // Populate form when data is fetched
  useEffect(() => {
    if (userData) {
      setFullName(userData.fullName || "");
      setEmail(userData.email || "");
      setCountryCode(userData.countryCode || "+91");
      
      // Set phone number (without country code)
      setPhone(userData.phone || "");
      
      // Set country for PhoneInput
      const countryCodeForInput = (userData.countryCode || "+91").replace("+", "");
      setSelectedCountry(countryCodeForInput === "91" ? "in" : countryCodeForInput.toLowerCase());
      
      // Set address fields
      if (userData.address) {
        setAddressLine1(userData.address.addressLine1 || "");
        setAddressLine2(userData.address.addressLine2 || "");
        setCity(userData.address.city || "");
        setState(userData.address.state || "");
        setCountry(userData.address.country || "");
        setPinCode(userData.address.pinCode || "");
      }
      
      setIsBlocked(userData.isBlocked || false);
    }
  }, [userData]);

  // Show error if API call fails
  useEffect(() => {
    if (error) {
      const errorMessage = (error as any)?.data?.message || "Failed to fetch user details. Please try again.";
      showError(errorMessage);
    }
  }, [error]);


  // Handle form submission
  const handleSave = async () => {
    if (!userId) {
      showError("User ID is missing");
      return;
    }

    try {
      const updateData = {
        id: userId,
        fullName: fullName.trim(),
        email: email.trim(),
        phone: phone.trim(),
        countryCode: countryCode.trim(),
        address: {
          addressLine1: addressLine1.trim(),
          addressLine2: addressLine2.trim(),
          city: city.trim(),
          state: state.trim(),
          country: country.trim(),
          pinCode: pinCode.trim(),
          type: 1,
          isDefault: true,
        },
        status: 2,
        isBlocked: isBlocked,
      };

      const response = await updateCustomer(updateData).unwrap();
      
      const successMessage = response?.message || "User updated successfully";
      showToast(successMessage);
      
      // Navigate back to user list
      navigate("/manage-users");
    } catch (error: any) {
      console.error("Error updating user:", error);
      const errorMessage =
        error?.data?.message ||
        error?.error?.data?.message ||
        error?.message ||
        "Failed to update user. Please try again.";
      showError(errorMessage);
    }
  };

  return (
    <>
      <Loader isLoad={isLoading || isUpdating} />
      <div className="main_title">
        <h1>Edit User</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
          <button onClick={() => navigate("/manage-users")}>Manage Users</button>{" "}
          - Edit User
        </p>
      </div>

      <div className="cards">
        <div className="form">
          <div className="gap_p">
            <div className="control_group w_33">
              <label htmlFor="full-name">Full Name</label>
              <TextField 
                id="full-name" 
                placeholder="Name" 
                hiddenLabel 
                fullWidth 
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
              />
            </div>

            <div className="control_group w_33">
              <label htmlFor="email">Email</label>
              <TextField 
                id="email" 
                type="email" 
                placeholder="Email" 
                hiddenLabel 
                fullWidth 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="control_group w_33">
              <label htmlFor="phone">Phone Number</label>
              <PhoneInput
                country={selectedCountry}
                placeholder="Enter Phone Number"
                enableSearch={false}
                autoFormat={false}
                inputStyle={{ width: "100%" }}
                value={countryCode.replace("+", "") + phone}
                onChange={(value, country) => {
                  const dialCode = (country as any)?.dialCode ? `+${(country as any).dialCode}` : "+91";
                  setCountryCode(dialCode);
                  
                  // Extract phone number without country code
                  const countryCodeDigits = dialCode.replace("+", "");
                  const phoneDigits = value.replace(/\D/g, "");
                  const phoneWithoutCode = phoneDigits.startsWith(countryCodeDigits) 
                    ? phoneDigits.substring(countryCodeDigits.length) 
                    : phoneDigits;
                  
                  setPhone(phoneWithoutCode);
                  setSelectedCountry((country as any)?.countryCode?.toLowerCase() || "in");
                }}
              />
            </div>
          </div>

          <div className="gap_p">
            <div className="control_group w_100">
              <label htmlFor="addressLine1">Address <span style={{ color: "red" }}>*</span></label>
              <TextField 
                id="addressLine1" 
                type="text" 
                placeholder="Enter Address" 
                hiddenLabel 
                fullWidth 
                value={addressLine1}
                onChange={(e) => setAddressLine1(e.target.value)}
              />
            </div>
            <div className="control_group w_100">
              <label htmlFor="addressLine2">Address Line 2</label>
              <TextField 
                id="addressLine2" 
                type="text" 
                placeholder="Enter Address Line 2" 
                hiddenLabel 
                fullWidth 
                value={addressLine2}
                onChange={(e) => setAddressLine2(e.target.value)}
              />
            </div>
            <div className="control_group w_33">
              <label htmlFor="country">Country <span style={{ color: "red" }}>*</span></label>
              <TextField 
                id="country" 
                type="text" 
                placeholder="Select Country" 
                hiddenLabel 
                fullWidth 
                value={country}
                onChange={(e) => setCountry(e.target.value)}
              />
            </div>
            <div className="control_group w_33">
              <label htmlFor="state">State <span style={{ color: "red" }}>*</span></label>
              <TextField 
                id="state" 
                type="text" 
                placeholder="Select State" 
                hiddenLabel 
                fullWidth 
                value={state}
                onChange={(e) => setState(e.target.value)}
              />
            </div>
            <div className="control_group w_33">
              <label htmlFor="city">City <span style={{ color: "red" }}>*</span></label>
              <TextField 
                id="city" 
                type="text" 
                placeholder="Select City" 
                hiddenLabel 
                fullWidth 
                value={city}
                onChange={(e) => setCity(e.target.value)}
              />
            </div>
            <div className="control_group w_33">
              <label htmlFor="pinCode">Pin Code <span style={{ color: "red" }}>*</span></label>
              <TextField 
                id="pinCode" 
                type="text" 
                placeholder="Enter Pin" 
                hiddenLabel 
                fullWidth 
                value={pinCode}
                onChange={(e) => setPinCode(e.target.value)}
              />
            </div>
          </div>

          <div className="form_btn">
            <Button 
              className="btn btn_primary" 
              onClick={handleSave}
              disabled={isUpdating || isLoading || !userId}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditUser;
